import React, { memo, KeyboardEvent, useContext } from 'react'
import moment from 'moment-timezone'
import { MapMarker } from 'transfix-ui/components/MapMarker'
import { LoadStatusTag } from 'apps/fleet-planner/components/atoms/LoadStatusTag'
import { UserContext } from 'apps/fleet-planner/context'
import { obscurePostDeliveryStatus } from 'apps/fleet-planner/pages/loads/utils'
import { ILoad, IStop } from 'apps/fleet-planner/types'
import {
  getFirstStop,
  getLastStop,
  formatStop,
  getOtherPickupStop,
  getOtherDropoff,
} from 'apps/fleet-planner/utils/stops'
import { CheckInIcon, CheckOutIcon, MultiStopIcon } from 'transfix-icons'
import { TAG_SIZE, TAG_STYLE, Tag, Avatar, Tooltip } from 'transfix-ui'
import { stopDayOfWeek, stopTimeShortNoTimeZone } from 'utils/appointmentHelpers'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'
import { KEY_VALUES } from 'constants/keyValues'
import { StopDateTime } from './StopDateTime'

const LoadSummary = memo<{ load: ILoad }>(({ load }) => {
  const firstStop = getFirstStop(load)
  const lastStop = getLastStop(load)
  const isMultiStop = load.stops.length > 2
  return (
    <strong className='fleet-load-summary'>
      {formatStop(firstStop)} {isMultiStop ? <MultiStopIcon className='multi-stop-icon' /> : '–'}{' '}
      {formatStop(lastStop)}
    </strong>
  )
})

interface ILoadCheckInOutProps {
  stop: IStop
  timeFormat: string
}

const LoadCheckInOut = memo<ILoadCheckInOutProps>(({ stop, timeFormat }) => {
  const { checkedInAt = '', checkedOutAt = '', displayTimezone, startAt = '' } = stop || {}
  const timezone = displayTimezone || FORMATS.defaultTimeZone
  const startMoment = moment(startAt)
  const isSameDay = (dateTime = '') => startMoment.isSame(dateTime, 'day')
  const checkInAndOutTime = (checkInOrOut = '') =>
    !isSameDay(checkInOrOut)
      ? `${stopDayOfWeek(checkInOrOut, timezone)} ${stopTimeShortNoTimeZone(
          checkInOrOut,
          undefined,
          timezone,
          true,
          timeFormat === FORMATS.militaryTimeFormatShort12H,
          timeFormat
        )}`
      : `${stopTimeShortNoTimeZone(
          checkInOrOut,
          undefined,
          timezone,
          true,
          timeFormat === FORMATS.militaryTimeFormatShort12H,
          timeFormat
        )}`
  const checkInOrOutTime = checkedOutAt || checkedInAt
  return (
    <>
      <div className='di pl2 pb1'>
        {(checkedInAt && !checkedOutAt) || (!checkedInAt && !checkedOutAt) ? (
          <CheckInIcon size='small' color={checkedInAt ? 'info' : 'coolGray5'} />
        ) : (
          <CheckOutIcon size='small' color={checkedOutAt ? 'info' : 'coolGray5'} />
        )}
      </div>
      <div className='text-info di pl2'>
        {checkedInAt || checkedOutAt ? (
          <span className='b text-success text-caption-semibold'>
            {checkInOrOutTime ? checkInAndOutTime(checkInOrOutTime) : ''}
          </span>
        ) : (
          <span className='b text-secondary text-caption-semibold'>{DISPLAY.emptyDateTime}</span>
        )}
      </div>
    </>
  )
})

interface ILoadStopProps {
  stop: IStop
  timeFormat: string
}

const LoadStop = memo<ILoadStopProps>(({ stop, timeFormat }) => {
  const { city, stateCode, stopType } = stop || {}
  return (
    <div className='row'>
      <div className='col-xs-1 pl0 mt2'>
        <MapMarker type={stopType} sequence={0} mini />
      </div>
      <div className='col-xs-11'>
        <span className='b'>
          {city}, {stateCode}
          <br />
        </span>
        <div className='flex flex-row items-center'>
          <StopDateTime separator='-' stop={stop} stopLocalTime timeFormat={timeFormat} />
          <LoadCheckInOut stop={stop} timeFormat={timeFormat} />
        </div>
      </div>
    </div>
  )
})

interface ILoadStopsProps {
  load: ILoad
  timeFormat: string
}

const LoadStops = memo<ILoadStopsProps>(({ load, timeFormat }) => {
  const { stops } = load || {}
  return (
    <>
      {stops.map(stop => (
        <LoadStop stop={stop} timeFormat={timeFormat} />
      ))}
    </>
  )
})

interface ILoadMetaProps {
  load: ILoad
  tooltip?: boolean
  timeFormat: string
}

const LoadMeta = memo<ILoadMetaProps>(({ load, timeFormat, tooltip }) => {
  const { currentStopSequence, isExternal, isSample, status: loadStatus, stops } = load || {}
  const status = obscurePostDeliveryStatus(loadStatus, isExternal)
  const firstStop = getFirstStop(load)
  const otherPickupStop = getOtherPickupStop(load)
  const otherDropoffStop = getOtherDropoff(load)
  const lastStop = getLastStop(load)
  const separator = <span>&nbsp; – &nbsp;</span>
  return (
    // Todo Why are timezones omitted in some of these StopDateTime components
    <>
      {isSample && (
        <Tag
          className='mr2'
          label='Sample Load'
          size={TAG_SIZE.xsmall}
          tagStyle={TAG_STYLE.success}
        />
      )}
      <LoadStatusTag
        status={status}
        size={TAG_SIZE.xsmall}
        stops={stops}
        currentStopSequence={Number(currentStopSequence)}
      />
      <span className='fleet-load-times'>
        &nbsp;&nbsp;
        <StopDateTime stop={firstStop} stopLocalTime={tooltip} timeFormat={timeFormat} />
        {otherPickupStop || otherDropoffStop ? (
          <>
            <span>
              &nbsp; – &nbsp;
              {otherPickupStop && (
                <>
                  <StopDateTime
                    stop={otherPickupStop}
                    stopLocalTime={tooltip}
                    timeFormat={timeFormat}
                  />
                  &nbsp; – &nbsp;
                </>
              )}
              {otherDropoffStop && (
                <>
                  <StopDateTime
                    stop={otherDropoffStop}
                    stopLocalTime={tooltip}
                    timeFormat={timeFormat}
                  />
                  &nbsp; – &nbsp;
                </>
              )}
            </span>
          </>
        ) : (
          <span>{separator}</span>
        )}
        <StopDateTime stop={lastStop} stopLocalTime={tooltip} timeFormat={timeFormat} />
      </span>
    </>
  )
})

const LoadDrivers = memo<{ load: ILoad; withNames?: boolean }>(
  ({ load: { drivers = [] }, withNames }) => (
    <>
      {drivers.map(({ name }) => (
        <Avatar key={name} name={name} size='xsmall' hideTooltip />
      ))}
      {withNames && (
        <span className='text-caption-semibold ml2'>
          {drivers.map(({ name }) => name).join(', ')}
        </span>
      )}
    </>
  )
)

interface IFleetLoad {
  load: ILoad
  overlapping?: boolean
  onClick: (load: ILoad) => void
}

const TOOLTIP_OFFSET = 8
const WRAPPER_CLASS = 'fleet-load-wrapper'
const overrideTooltipPosition = (
  { left, top }: { left: number; top: number },
  e: Event,
  currentTarget: Element
): { left: number; top: number } => {
  const tooltip = currentTarget.classList.contains(WRAPPER_CLASS)
    ? currentTarget.nextElementSibling
    : currentTarget
  const target = currentTarget.classList.contains(WRAPPER_CLASS)
    ? currentTarget
    : currentTarget.previousElementSibling
  if (tooltip) {
    const tooltipRect = tooltip.getBoundingClientRect()
    let tooltipLeft = tooltipRect.left
    if (target) {
      const targetRect = target.getBoundingClientRect()
      tooltipLeft = targetRect.left + targetRect.width / 2 - tooltipRect.width / 2
    }

    if (window.innerWidth <= tooltipRect.width + tooltipLeft + TOOLTIP_OFFSET) {
      left = window.innerWidth - tooltipRect.width - TOOLTIP_OFFSET
    }
  }
  return { left, top }
}

export const FleetLoad = memo<IFleetLoad>(({ load, onClick, overlapping }) => {
  const { timeFormats } = useContext(UserContext)
  const {
    currentStopSequence,
    drivers,
    externalId,
    id,
    isExternal,
    isSample,
    status: loadStatus,
    stops,
  } = load
  const status = obscurePostDeliveryStatus(loadStatus, isExternal)
  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.key === KEY_VALUES.ENTER) onClick(load)
  }
  const handleClick = () => onClick(load)
  const isTeamLoad = (drivers || []).length > 1
  return (
    <Tooltip
      id={`load-tooltip-${id}`}
      position='top'
      delayShow={300}
      overridePosition={overrideTooltipPosition}
      type='light'
      trigger={
        <div
          className='fleet-load-wrapper'
          onKeyDown={handleKeyDown}
          onClick={handleClick}
          role='button'
          tabIndex={0}>
          <div className='fleet-load'>
            {overlapping ? (
              <>
                <LoadSummary load={load} />
                <span className='ml2'>
                  {isSample && (
                    <Tag
                      className='mr2'
                      label='Sample Load'
                      size={TAG_SIZE.xsmall}
                      tagStyle={TAG_STYLE.success}
                    />
                  )}
                  <LoadStatusTag
                    status={status}
                    size={TAG_SIZE.xsmall}
                    stops={stops}
                    currentStopSequence={Number(currentStopSequence)}
                  />
                </span>
                {isTeamLoad && (
                  <span className='ml2'>
                    <LoadDrivers load={load} />
                  </span>
                )}
              </>
            ) : (
              <>
                <div>
                  <LoadSummary load={load} />
                </div>
                <div className='fleet-load-meta-wrapper'>
                  <LoadMeta load={load} timeFormat={timeFormats.listStopAppointment} />
                  {isTeamLoad && (
                    <span className='ml2'>
                      <LoadDrivers load={load} />
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      }>
      <div className='fleet-load-tooltip'>
        <div className='flex flex-row items-center mb2'>
          <span className='body-medium b pr2'>Load # {externalId || DISPLAY.empty}</span>
          <LoadStatusTag
            status={status}
            size={TAG_SIZE.xsmall}
            stops={stops}
            currentStopSequence={Number(currentStopSequence)}
          />
        </div>
        {isTeamLoad && (
          <div className='mb4'>
            <LoadDrivers load={load} withNames />
          </div>
        )}
        <div className='col-xs-12'>
          <LoadStops timeFormat={timeFormats.listStopAppointment} load={load} />
        </div>
      </div>
    </Tooltip>
  )
})
